import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { API_URL, API_BASE_URL } from "../config/api";
import { 
  Star, 
  MessageSquare, 
  Calendar,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Loader2,
  Info,
  AlertTriangle,
  User,
  Package,
  Lightbulb,
  Mail,
  Phone,
  ExternalLink
} from "lucide-react";


const RATING_CONFIG = {
  1: { label: "Very Unsatisfied", color: "bg-red-500", emoji: "😟" },
  2: { label: "Unsatisfied", color: "bg-orange-500", emoji: "😕" },
  3: { label: "Neutral", color: "bg-yellow-500", emoji: "😐" },
  4: { label: "Satisfied", color: "bg-lime-500", emoji: "🙂" },
  5: { label: "Very Satisfied", color: "bg-green-500", emoji: "😊" },
};

// Human labels for the Kaizen survey category keys persisted on each survey.
// Falls back to a title-cased version of the raw key when not in the map.
const CATEGORY_LABELS = {
  overall_experience: "Overall experience",
  operations: "Operations & handling",
  communication: "Communication & responsiveness",
  on_time_delivery: "On-time delivery",
  documentation: "Documentation & paperwork",
  pricing: "Pricing & invoicing",
  problem_solving: "Problem solving",
  customer_service: "Customer service",
  tracking_visibility: "Tracking & visibility",
  professionalism: "Professionalism",
  value_for_money: "Value for money",
};
const prettyCategory = (k) =>
  CATEGORY_LABELS[k] ||
  String(k).replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

function StarRating({ rating, size = "md" }) {
  const sizeClass = size === "lg" ? "h-6 w-6" : "h-4 w-4";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
        />
      ))}
    </div>
  );
}

function FeedbackCard({ feedback, viewerIsAdmin }) {
  const ratingInfo = RATING_CONFIG[feedback.rating] || RATING_CONFIG[3];
  const isLowRating = feedback.rating <= 3;
  const categories = feedback.categories || {};
  const categoryEntries = Object.entries(categories);
  const lowCategories = categoryEntries.filter(([, v]) => Number(v) <= 3);

  return (
    <div
      className={`border rounded-lg p-4 ${isLowRating ? 'bg-orange-50 border-orange-200' : 'bg-white'}`}
      data-testid="feedback-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full ${ratingInfo.color} flex items-center justify-center text-white text-2xl`}>
            {ratingInfo.emoji}
          </div>
          <div>
            <h4 className="font-semibold text-slate-800">
              {viewerIsAdmin && feedback.customer ? feedback.customer : "Customer Feedback"}
            </h4>
            <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
              <Calendar className="h-3 w-3" />
              <span>{feedback.date ? new Date(feedback.date).toLocaleDateString() : "—"}</span>
              {viewerIsAdmin && feedback.pf_number && (
                <>
                  <span className="text-slate-300">·</span>
                  <Package className="h-3 w-3" />
                  <span className="font-mono">{feedback.pf_number}</span>
                </>
              )}
              {viewerIsAdmin && feedback.customer_email && (
                <>
                  <span className="text-slate-300">·</span>
                  <User className="h-3 w-3" />
                  <span className="font-mono">{feedback.customer_email}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          <StarRating rating={feedback.rating} />
          <Badge className={`${ratingInfo.color} text-white mt-1`}>
            {ratingInfo.label}
          </Badge>
          {feedback.would_recommend !== null && feedback.would_recommend !== undefined && (
            <div className="mt-1 text-xs flex items-center justify-end gap-1">
              {feedback.would_recommend ? (
                <><ThumbsUp className="h-3 w-3 text-green-600" /><span className="text-green-700">Would recommend</span></>
              ) : (
                <><ThumbsDown className="h-3 w-3 text-rose-600" /><span className="text-rose-700">Would NOT recommend</span></>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Per-question breakdown — the headline answer to "what gave me 3"? */}
      {categoryEntries.length > 0 && (
        <div className="mt-3 pt-3 border-t" data-testid="feedback-categories">
          <p className="text-xs font-medium text-slate-600 mb-2">
            Per-question scores
            {feedback.average_rating != null && (
              <span className="text-slate-400 font-normal"> · avg {feedback.average_rating.toFixed(1)}</span>
            )}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {categoryEntries.map(([key, val]) => {
              const score = Number(val) || 0;
              const tone = score <= 2 ? "text-rose-700 bg-rose-50 border-rose-200"
                          : score === 3 ? "text-amber-700 bg-amber-50 border-amber-200"
                          : "text-emerald-700 bg-emerald-50 border-emerald-200";
              return (
                <div
                  key={key}
                  className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded border text-xs ${tone}`}
                  data-testid={`cat-score-${key}`}
                >
                  <span className="truncate" title={prettyCategory(key)}>{prettyCategory(key)}</span>
                  <span className="flex items-center gap-1 flex-shrink-0">
                    <StarRating rating={score} />
                    <span className="font-mono font-semibold">{score}</span>
                  </span>
                </div>
              );
            })}
          </div>
          {lowCategories.length > 0 && (
            <p className="text-[11px] text-amber-700 mt-2 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Low scores on: {lowCategories.map(([k]) => prettyCategory(k)).join(", ")}
            </p>
          )}
        </div>
      )}

      {(feedback.reason || feedback.additional_feedback || feedback.improvement_suggestion) && (
        <div className="mt-3 pt-3 border-t space-y-2">
          {feedback.reason && (
            <div className="bg-white rounded-lg p-3 border">
              <p className="text-xs text-slate-500 mb-1 font-medium">Feedback category:</p>
              <p className="text-sm">{feedback.reason}</p>
            </div>
          )}
          {feedback.additional_feedback && (
            <div className="bg-white rounded-lg p-3 border">
              <p className="text-xs text-slate-500 mb-1 font-medium">Additional comments:</p>
              <p className="text-sm">{feedback.additional_feedback}</p>
            </div>
          )}
          {feedback.improvement_suggestion && (
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
              <p className="text-xs text-amber-700 mb-1 font-medium flex items-center gap-1">
                <Lightbulb className="h-3 w-3" />
                Improvement suggestion:
              </p>
              <p className="text-sm text-amber-900">{feedback.improvement_suggestion}</p>
            </div>
          )}
        </div>
      )}

      {/* Quick-action follow-up row — admin-only because only admins have
          the identity / contact fields populated. Renders even when the
          rating is high so the path is consistent. */}
      {viewerIsAdmin && (feedback.pf_id || feedback.customer_email || feedback.customer_phone) && (
        <div
          className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2"
          data-testid="feedback-followup-actions"
        >
          <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-1">
            Follow up{feedback.customer ? ` with ${feedback.customer}` : ""}:
          </span>

          {feedback.pf_id && (
            <a
              href={`/position-folders?id=${feedback.pf_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-800 text-xs font-medium"
              data-testid="followup-open-pf"
              title={`Open ${feedback.pf_number || "this PF"}`}
            >
              <Package className="h-3.5 w-3.5" />
              Open PF
              <ExternalLink className="h-3 w-3 opacity-60" />
            </a>
          )}

          {feedback.customer_email && (
            <a
              href={`mailto:${feedback.customer_email}?subject=${encodeURIComponent(
                `Following up on your feedback for ${feedback.pf_number || "your shipment"}`
              )}&body=${encodeURIComponent(
                `Hello,\n\nThank you for taking the time to rate your recent shipment` +
                (feedback.pf_number ? ` (${feedback.pf_number})` : "") +
                `.\n\nWe noticed you gave us ${feedback.rating}/5` +
                (feedback.improvement_suggestion ? ` and mentioned: "${feedback.improvement_suggestion}".` : ".") +
                `\n\nI would love to understand more about how we can do better. Could we hop on a quick call?\n\nBest regards,\nEuropacific Operations`
              )}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 text-xs font-medium"
              data-testid="followup-email"
              title={`Email ${feedback.customer_email}`}
            >
              <Mail className="h-3.5 w-3.5" />
              Email customer
            </a>
          )}

          {feedback.customer_phone && (
            <a
              href={`tel:${feedback.customer_phone.replace(/[^+\d]/g, "")}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-violet-50 hover:bg-violet-100 border border-violet-200 text-violet-800 text-xs font-medium"
              data-testid="followup-call"
              title={`Call ${feedback.customer_phone}`}
            >
              <Phone className="h-3.5 w-3.5" />
              Call <span className="font-mono">{feedback.customer_phone}</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function MyFeedbackPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchMyFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/hr-performance/my-feedback-v2`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch feedback:", error);
      toast.error("Failed to load your feedback");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMyFeedback();
  }, [fetchMyFeedback]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  const avgRating = data?.average_rating || 0;
  const totalSurveys = data?.total_surveys || 0;
  const surveysSent = data?.surveys_sent || 0;
  const surveysPending = data?.surveys_pending || 0;
  const feedback = data?.feedback || [];
  const ratingDist = data?.rating_distribution || {};
  
  const lowRatings = feedback.filter(f => f.rating <= 3);
  const highRatings = feedback.filter(f => f.rating >= 4);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] to-[#004488] text-white px-6 py-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <MessageSquare className="h-6 w-6" />
                My Customer Feedback
              </h1>
              <p className="text-blue-200 text-sm mt-1">
                See how customers rated your service
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={fetchMyFeedback}
              className="text-white border-white/30 hover:bg-white/10"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800">How this works</h3>
              <p className="text-sm text-blue-700 mt-1">
                Customer satisfaction surveys are sent <strong>automatically</strong> and <strong>randomly</strong> to 
                ~18% of customers after their shipment is invoiced. You cannot predict which shipments will be rated, 
                ensuring fair and unbiased feedback. This rating contributes to your performance score.
              </p>
              <p className="text-sm text-blue-600 mt-2">
                <strong>Note:</strong>{" "}
                {data?.viewer_is_admin
                  ? "You are viewing this page as an admin — customer names and PF numbers are shown so you can follow up directly. Regular staff see this feedback anonymised."
                  : "Feedback is anonymised — customer names and shipment details are not shown to maintain professional relationships."}
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Average Rating</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-3xl font-bold">{avgRating.toFixed(1)}</span>
                    <Star className={`h-6 w-6 ${avgRating >= 4 ? 'fill-yellow-400 text-yellow-400' : avgRating >= 3 ? 'fill-yellow-300 text-yellow-300' : 'text-slate-300'}`} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Responses</p>
                  <p className="text-3xl font-bold" data-testid="my-feedback-total">{totalSurveys}</p>
                  <p className="text-xs text-slate-400">
                    of {surveysSent} sent{surveysPending > 0 && ` · ${surveysPending} pending`}
                  </p>
                </div>
                <MessageSquare className="h-10 w-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Satisfied (4-5)</p>
                  <p className="text-3xl font-bold text-green-600">{highRatings.length}</p>
                </div>
                <ThumbsUp className="h-10 w-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={lowRatings.length > 0 ? "border-orange-300 bg-orange-50" : ""}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Needs Attention (1-3)</p>
                  <p className="text-3xl font-bold text-orange-600">{lowRatings.length}</p>
                </div>
                <AlertTriangle className="h-10 w-10 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Rating Distribution */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Rating Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((rating) => {
                const count = ratingDist[rating] || 0;
                const pct = totalSurveys > 0 ? (count / totalSurveys) * 100 : 0;
                const config = RATING_CONFIG[rating];
                return (
                  <div key={rating} className="flex items-center gap-3">
                    <div className="w-8 flex items-center gap-1">
                      <span className="font-medium">{rating}</span>
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    </div>
                    <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${config.color}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-12 text-right text-sm text-slate-600">{count}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Feedback List */}
        <Card>
          <CardHeader>
            <CardTitle>All Feedback ({feedback.length})</CardTitle>
            <CardDescription>
              Customer ratings and comments for your shipments
            </CardDescription>
          </CardHeader>
          <CardContent>
            {feedback.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No feedback received yet</p>
                <p className="text-sm mt-2">
                  Surveys are sent randomly to customers - you'll see feedback as customers respond.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {feedback.map((fb, idx) => (
                  <FeedbackCard key={idx} feedback={fb} viewerIsAdmin={!!data?.viewer_is_admin} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
